import { Button, Icon24Alert, Typography } from "@cur8/maneki";
import { Visit } from "@cur8/rich-entity";
import { useNav } from "@pomle/react-router-paths";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { formatTimeRange } from "lib/formatDateTime";
import { formatLegalName } from "lib/formatMemberName";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import styles from "./styles.module.sass";

export function WrongLocationView({ visit }: { visit: Visit }) {
  const patientQuery = usePatientQuery({ patientId: visit.patientId });
  const patient = patientQuery.data;

  const fullName = formatLegalName(patient?.name);
  // First name only, deliberately: the preferred name belongs on Active
  // Sessions, not here. The fallback is what this line actually needed —
  // it opens a sentence as a possessive, so without one it renders as a
  // stranded "'s appointment is scheduled at ..." while the query loads.
  const memberName = patient?.name?.firstName?.trim() || "This member";
  const countryCode = visit.site.address.countryCode?.toUpperCase();
  const time = formatTimeRange(visit.startTime, visit.endTime, countryCode);

  const { data: visitHistory } = useVisitHistoryQuery({
    patientId: visit.patientId,
  });
  const visitTypeLabel =
    visit != null && visitHistory != null
      ? generateVisitTypeLabel({ visit, visitHistory })
      : undefined;

  const nav = {
    start: useNav(paths.root),
  };

  return (
    <PageFrameContent>
      <InformationLayout
        align="start"
        content={
          <section className={styles.section}>
            <Typography variant="display-s">Wrong location</Typography>
            <Typography variant="body-m" color="subtle">
              {memberName}'s appointment is scheduled at {visit.site.siteName}.
            </Typography>
            <div className={styles.box}>
              <div className={styles.row}>
                <Typography variant="title-s">
                  Neko Health, {visit.site.siteName}
                </Typography>
                <div className={styles.icon}>
                  <Icon24Alert />
                </div>
              </div>
              <div className={styles.divider} />
              <div className={styles.card}>
                <div className={styles.row}>
                  <Typography variant="label-m">{fullName}</Typography>
                </div>
                <div className={styles.row}>
                  <Typography variant="body-m" color="subtle">
                    {visitTypeLabel}
                  </Typography>
                  <div className={styles.status}>
                    <div className={styles.statusIndicator} />
                    <Typography variant="body-m" color="subtle">
                      {time}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </section>
        }
        buttons={
          <Button
            variant="secondary"
            onPress={() => nav.start.go({})}
            fillContainer
          >
            Back
          </Button>
        }
      />
    </PageFrameContent>
  );
}
